import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, MessageCircle, Plus } from 'lucide-react';
import { useChatStore } from '@/stores/chat-store';
import { useAuthStore } from '@/stores/auth-store';
import { ConversationStatus, Conversation } from '@/services/chat.service';
import { departmentsService } from '@/services/departments.service';
import { ConversationItem } from './conversation-item';
import { NewConversationDialog } from './new-conversation-dialog';
import { cn } from '@caruso/ui';
import { Button } from '@caruso/ui';

interface ConversationListProps {
  onClose?: () => void;
}

const statusOptions = [
  { value: '', label: 'Todos os status' },
  { value: 'OPEN', label: 'Abertas' },
  { value: 'IN_PROGRESS', label: 'Em Atendimento' },
  { value: 'WAITING', label: 'Aguardando' },
  { value: 'CLOSED', label: 'Fechadas' },
];

export function ConversationList({ onClose }: ConversationListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);

  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  const {
    conversations,
    selectedConversation,
    stats,
    isLoading,
    setFilters,
    selectConversation,
    fetchConversations,
    fetchStats
  } = useChatStore();

  // Callback quando nova conversa é criada
  const handleConversationCreated = (conversation: Conversation) => {
    fetchConversations();
    fetchStats();
    selectConversation(conversation);
    onClose?.();
  };

  // Buscar departamentos - admins veem todos, usuarios normais veem apenas os seus
  const { data: departmentsData } = useQuery({
    queryKey: ['departments', isAdmin ? 'all' : 'my'],
    queryFn: () => isAdmin ? departmentsService.getAll() : departmentsService.getMyDepartments(),
  });

  const departments = departmentsData || [];

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Aplicar filtros quando mudarem
  useEffect(() => {
    setFilters({
      search: debouncedSearch || undefined,
      departmentId: selectedDepartment || undefined,
      status: selectedStatus as ConversationStatus || undefined,
    });
  }, [debouncedSearch, selectedDepartment, selectedStatus, setFilters]);

  const handleSelectConversation = (conversation: any) => {
    selectConversation(conversation);
    onClose?.();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDepartment('');
    setSelectedStatus('');
  };

  const hasActiveFilters = searchQuery || selectedDepartment || selectedStatus;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-brand-gold/10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-serif text-brand-gold">Conversas</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsNewConversationOpen(true)}
              className="text-brand-gold hover:bg-brand-gold/10"
              title="Nova conversa"
            >
              <Plus className="w-4 h-4" />
            </Button>
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="md:hidden text-brand-ivory/70 hover:text-brand-gold"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ivory/50" />
          <input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:border-brand-gold/30 text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ivory/50 hover:text-brand-ivory"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters - Status and Department */}
        <div className="flex gap-2">
          {/* Status Select */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="flex-1 px-3 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg text-brand-ivory text-sm focus:outline-none focus:border-brand-gold/30"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Department Select - mostra se admin ou se usuario tem mais de 1 departamento */}
          {(isAdmin || departments.length > 1) && (
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="flex-1 px-3 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg text-brand-ivory text-sm focus:outline-none focus:border-brand-gold/30"
            >
              <option value="">{isAdmin ? 'Todos departamentos' : 'Meus departamentos'}</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="mt-2 text-xs text-brand-gold hover:underline"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="px-4 py-2 bg-brand-smoke/30 border-b border-brand-gold/10">
          <div className="flex items-center justify-between text-xs text-brand-ivory/50">
            <span>{stats.unread} nao lidas</span>
            <span>{stats.total} conversas</span>
          </div>
        </div>
      )}

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin w-6 h-6 border-2 border-brand-gold/30 border-t-brand-gold rounded-full" />
          </div>
        ) : !conversations || conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-brand-ivory/50 text-sm">
            <MessageCircle className="w-8 h-8 mb-2 opacity-50" />
            <p>Nenhuma conversa encontrada</p>
          </div>
        ) : (
          <div className="divide-y divide-brand-gold/5">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={selectedConversation?.id === conversation.id}
                onClick={() => handleSelectConversation(conversation)}
              />
            ))}
          </div>
        )}
      </div>

      {/* New Conversation Dialog */}
      <NewConversationDialog
        isOpen={isNewConversationOpen}
        onClose={() => setIsNewConversationOpen(false)}
        onConversationCreated={handleConversationCreated}
      />
    </div>
  );
}
